import { useState, useEffect, useCallback } from 'react';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import constate from 'constate';
import jwtDecode from 'jwt-decode';
import { omit } from 'lodash';
import Cookies from 'universal-cookie';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

import * as Backend from '../utils/Backend';

import {
    getEnvironmentVariable
} from './GetEnvVar';

const cookies = new Cookies();

const sentryDSN = (typeof window !== 'undefined' && window?._spacefill_env_?.sentryDsn) || import.meta.env?.VITE_ADMIN_CONSOLE_SENTRY_DSN || import.meta.env?.VITE_EXTERNAL_CONSOLE_SENTRY_DSN;

const GET_USER_QUERY = gql`
    query getUser($userId: UUID!) {
        user(id: $userId) {
            id
            role
            email
            gender
            lastName
            firstName
            title
            phoneNumber
            preferredLanguage
            customer {
                id
                company
                createdAt
                hasAnalyticsEnabled
                orderManagementEnabled
                eachBarcodeEnabled
                customDashboard
                orderRoutingEnabled
            }
            logisticProviderId
            logisticProvider {
                id
                siren
                company
                hasAnalyticsEnabled
                warehouses {
                    nodes {
                        id
                        reference
                    }
                }
                customDashboard
            }
            userPermissions(
                filter: {customerWarehouseConfigurations: {logisticManagementEnabled: {equalTo: true}}}
            ){
                nodes {
                    customerWarehouseConfigurations {
                        id
                        customerId
                        warehouseId
                        warehouseNameForShipper
                    }
                }
            }
        }
    }
`;

function useUser() {
    const { t, i18n } = useTranslation();
    const queryParams = new URLSearchParams(global.location.search);
    const envPrefix = getEnvironmentVariable('envPrefix');

    let jwtToken;
    let jwtTokenImpersonator;

    switch (envPrefix) {
    case 'ADMIN-CONSOLE':
        jwtToken = cookies.get('jwtTokenAdminConsole');
        break;
    case 'EXTERNAL-CONSOLE':
        jwtToken = cookies.get('jwtTokenExternalConsole');
        jwtTokenImpersonator = cookies.get('jwtTokenExternalConsoleImpersonator');
        break;
    default:
        break;
    }
    let decodedJwt;
    let decodedJwtImpersonator;
    try {
        if (jwtToken) {
            decodedJwt = jwtDecode(jwtToken);
        }
        if (jwtTokenImpersonator) {
            decodedJwtImpersonator = jwtDecode(jwtTokenImpersonator);
        }
    } catch (error) {
        console.error(error);
    }

    const [user, setUser] = useState(
        jwtToken && decodedJwt
            ? {
                logged: true,
                jwtToken: jwtToken,
                id: decodedJwt?.user_id,
                impersonator: jwtTokenImpersonator && decodedJwtImpersonator
                    ? {
                        jwtToken: jwtTokenImpersonator,
                        id: decodedJwtImpersonator?.user_id
                    }
                    : undefined
            }
            : {
                logged: false,
                jwtToken: undefined,
                id: undefined
            }
    );

    const [authenticate] = useMutation(gql`
        mutation authenticate($email: String!, $password: String!, $applicationName: ApplicationsNameEnum) {
            authenticate(
                input: {
                    email: $email
                    password: $password
                    applicationName: $applicationName
                }
            ) {
                jwtToken
            }
        }
    `);
    const [impersonate] = useMutation(gql`
        mutation impersonate($impersonateUserId: UUID!) {
            impersonate(input: {impersonateUserId: $impersonateUserId}) {
                jwtToken
            }
        }
    `);
    const [switchAccount] = useMutation(gql`
        mutation reauthenticateWithRole($role: UsersRoleEnum!) {
            reauthenticateWithRole(input: {role: $role}) {
                jwtToken
            }
        }
    `);

    const [getUser] = useLazyQuery(
        GET_USER_QUERY
        , {
            onCompleted: async(result) => {
                let newUser = Object.assign({}, user);
                let { customer, logisticProvider } = Object.assign({}, (result?.user));
                let userFeatures = [];
                try {
                    userFeatures = await Backend.call(['users', 'features'], {}, 'GET');
                    if (!result || !result?.user) {
                        toast.error(t('This user doesn\'t exist or have been removed'));
                        logout();
                        return;
                    }
                } catch (e) {
                    console.error('Could not get user features.');
                    // fall through
                }

                if (!customer && (result?.user?.role) === 'SHIPPER') {
                    if (!(newUser?.customer?.isLoaded ?? false)) {
                        customer = { isLoaded: true };
                        toast.error(t('Expired session. Please refresh this page'));
                        console.error('Customer not retrieved within UserContext');
                    } else {
                        customer = { isLoaded: false };
                    }
                } else {
                    customer = {
                        ...customer,
                        isLoaded: true
                    };
                }
                if (!logisticProvider && (result?.user?.role) === 'PROVIDER') {
                    if (!(newUser?.logisticProvider?.isLoaded ?? false)) {
                        logisticProvider = { isLoaded: true };
                        toast.error(t('Expired session. Please refresh this page'));
                        console.error('Logistic provider not retrieved within UserContext');
                    } else {
                        logisticProvider = { isLoaded: false };
                    }
                } else {
                    logisticProvider = {
                        ...logisticProvider,
                        isLoaded: true
                    };
                }

                if ((result?.user?.preferredLanguage ?? false)) {
                    if (queryParams.get('lng') === null) {
                        i18n.changeLanguage((result?.user?.preferredLanguage));
                    }
                    localStorage.setItem('spacefillLocale', (result?.user?.preferredLanguage));
                    newUser.preferredLanguage = (result?.user?.preferredLanguage);
                }
                newUser = {
                    ...newUser,
                    ...omit((result?.user ?? {}), ['customer', 'logisticProvider', 'userPermissions']),
                    customer,
                    logisticProvider,
                    customerWarehouseConfigurationsWithLogisticManagement: (result?.user?.userPermissions?.nodes ?? []).map(({ customerWarehouseConfigurations }) => ({ ...customerWarehouseConfigurations })),
                    features: {
                        hasAnalyticsEnabled: (customer?.hasAnalyticsEnabled || logisticProvider?.hasAnalyticsEnabled) ?? false,
                        orderManagementEnabled: customer?.orderManagementEnabled ?? false,
                        customDashboard: customer?.customDashboard ?? logisticProvider?.customDashboard ?? null,
                        orderRoutingEnabled: customer?.orderRoutingEnabled ?? false
                    },
                    toggleFeatures: userFeatures
                };
                if (typeof sentryDSN === 'string' && sentryDSN !== '') {
                    Sentry.setUser({
                        id: newUser.id,
                        email: newUser.email
                    });
                }
                setUser(newUser);
            }
        }
    );

    const login = async(email, password, impersonateUserId, rememberMe) => {
        const currentDate = new Date();
        const resultAuthenticate = await authenticate({
            variables: {
                email: email,
                password: password,
                applicationName: envPrefix.replace('-', '_')
            },
            context: {
                customerNoAuth: true
            }
        });

        if (jwtDecode(resultAuthenticate.data.authenticate.jwtToken).user_id === '00000000-0000-0000-0000-000000000000') {
            setUser({
                logged: false,
                jwtToken: resultAuthenticate.data.authenticate.jwtToken,
                id: undefined
            });
            throw new Error(jwtDecode(resultAuthenticate.data.authenticate.jwtToken).role);
        }

        let user = {
            logged: true,
            jwtToken: resultAuthenticate.data.authenticate.jwtToken,
            id: jwtDecode(resultAuthenticate.data.authenticate.jwtToken).user_id
        };
        switch (envPrefix) {
        case 'ADMIN-CONSOLE':
            cookies.set('jwtTokenAdminConsole', resultAuthenticate.data.authenticate.jwtToken, {
                domain: document.domain,
                path: '/',
                expires: rememberMe ? new Date(currentDate.getFullYear() + 10, currentDate.getMonth(), currentDate.getDay()) : undefined
            });
            break;
        case 'EXTERNAL-CONSOLE':
            cookies.set('jwtTokenExternalConsole', resultAuthenticate.data.authenticate.jwtToken, {
                domain: document.domain,
                path: '/',
                expires: rememberMe ? new Date(currentDate.getFullYear() + 10, currentDate.getMonth(), currentDate.getDay()) : undefined
            });
            break;
        default:
            break;
        }
        cookies.set('jwtTokenRememberMe', !!rememberMe, {
            domain: document.domain,
            path: '/',
            expires: undefined
        });

        if (impersonateUserId) {
            const resultImpersonate = await impersonate({
                variables: {
                    impersonateUserId: impersonateUserId
                }
            });
            switch (envPrefix) {
            case 'ADMIN-CONSOLE':
                cookies.set('jwtTokenAdminConsoleImpersonator', resultAuthenticate.data.authenticate.jwtToken, {
                    domain: document.domain,
                    path: '/',
                    expires: rememberMe ? new Date(currentDate.getFullYear() + 10, currentDate.getMonth(), currentDate.getDay()) : undefined
                });
                cookies.set('jwtTokenAdminConsole', resultImpersonate.data.impersonate.jwtToken, {
                    domain: document.domain,
                    path: '/',
                    expires: rememberMe ? new Date(currentDate.getFullYear() + 10, currentDate.getMonth(), currentDate.getDay()) : undefined
                });
                break;
            case 'EXTERNAL-CONSOLE':
                cookies.set('jwtTokenExternalConsoleImpersonator', resultAuthenticate.data.authenticate.jwtToken, {
                    domain: document.domain,
                    path: '/',
                    expires: rememberMe ? new Date(currentDate.getFullYear() + 10, currentDate.getMonth(), currentDate.getDay()) : undefined
                });
                cookies.set('jwtTokenExternalConsole', resultImpersonate.data.impersonate.jwtToken, {
                    domain: document.domain,
                    path: '/',
                    expires: rememberMe ? new Date(currentDate.getFullYear() + 10, currentDate.getMonth(), currentDate.getDay()) : undefined
                });
                break;
            default:
                break;
            }

            user = {
                logged: true,
                jwtToken: resultImpersonate.data.impersonate.jwtToken,
                id: jwtDecode(resultImpersonate.data.impersonate.jwtToken).user_id,
                impersonator: {
                    jwtToken: resultAuthenticate.data.authenticate.jwtToken,
                    id: jwtDecode(resultAuthenticate.data.authenticate.jwtToken).user_id
                }
            };
        }
        getUser({
            variables: {
                userId: user.id
            }
        });

        setUser(user);
    };

    const logout = useCallback(() => {
        switch (envPrefix) {
        case 'ADMIN-CONSOLE':
            cookies.remove('jwtTokenAdminConsole', {
                domain: document.domain,
                path: '/'
            });
            break;
        case 'EXTERNAL-CONSOLE':
            cookies.remove('jwtTokenExternalConsole', {
                domain: document.domain,
                path: '/'
            });
            cookies.remove('jwtTokenExternalConsoleImpersonator', {
                domain: document.domain,
                path: '/'
            });
            break;
        default:
            break;
        }
        cookies.remove('jwtTokenRememberMe', {
            domain: document.domain,
            path: '/'
        });
        setUser({
            logged: false,
            jwtToken: undefined,
            id: undefined
        });
    }, [envPrefix]);

    const switchRole = async(role) => {
        const currentDate = new Date();
        const resultAuthenticate = await switchAccount({
            variables: {
                role: role
            }
        });

        const jwtToken = resultAuthenticate?.data?.reauthenticateWithRole?.jwtToken;
        if (jwtDecode(jwtToken).user_id === '00000000-0000-0000-0000-000000000000') {
            setUser({
                logged: false,
                jwtToken,
                id: undefined
            });
            throw new Error(jwtDecode(jwtToken).role);
        }

        const user = {
            logged: true,
            jwtToken,
            id: jwtDecode(jwtToken).user_id
        };
        const rememberMe = cookies.get('jwtTokenRememberMe');
        cookies.set('jwtTokenExternalConsole', jwtToken, {
            domain: document.domain,
            path: '/',
            expires: rememberMe ? new Date(currentDate.getFullYear() + 10, currentDate.getMonth(), currentDate.getDay()) : undefined
        });

        getUser({
            variables: {
                userId: user.id
            }
        });
    };

    useEffect(() => {
        // Token is expired
        if ((decodedJwt?.exp ?? false) && new Date(decodedJwt?.exp * 1000) < Date.now()) {
            toast.error(t('Your session has expired, please sign in again'));
            logout();
            return;
        }

        if (user.id) {
            if (posthog.config) {
                posthog.init(
                    posthog.config.token,
                    {
                        api_host: posthog.config.api_host,
                        loaded: (posthog) => {
                            posthog.identify(user.id);
                        }
                    }
                );
            }
        }
        if (user.id && !(user?.customer?.isLoaded || user?.logisticProvider?.isLoaded)) {
            getUser({
                variables: {
                    userId: user.id
                }
            });
        }

        if (user?.customer?.isLoaded || user?.logisticProvider?.isLoaded) {
            if (posthog.people && envPrefix === 'EXTERNAL-CONSOLE') {
                posthog.people.set({
                    id: (user?.id),
                    email: (user?.email),
                    role: (user?.role),
                    logistic_provider_id: (user?.logisticProvider?.id),
                    customer_id: (user?.customer?.id),
                    impersonator_user_id: (user?.impersonator?.id),
                    is_incarned: (user?.impersonator?.id) != null,
                    is_spacefill_user: ['ADMIN', 'SALES', 'OPS'].includes((user?.role)),
                    has_order_management_enabled: user?.customer?.orderManagementEnabled ?? false
                });
            }
        }
    }, [getUser, user, envPrefix, decodedJwt?.exp, logout, t]);
    return { user, setUser, login, logout, switchRole, getUser };
}

const [UserProvider, useUserContext] = constate(useUser);

export {
    UserProvider,
    useUserContext,
    GET_USER_QUERY
};
